import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Activity,
  Zap,
  Settings
} from 'lucide-react';
import axios from '../../utils/axios';

const EvaluationTimeline = ({
  runId,
  autoRefresh = true,
  refreshInterval = 2000,
  showDetails = true,
  compact = false
}) => {
  const [timeline, setTimeline] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch timeline data
  const fetchTimeline = useCallback(async () => {
    if (!runId) return;

    try {
      setIsRefreshing(true);
      const response = await axios.get(`/evaluations/runs/${runId}/timeline`);

      const data = response.data;
      setTimeline(data.timeline || []);
      setSummary(data.summary || null);
      setError(null);
    } catch (err) {
      console.error('Error fetching timeline:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [runId]);

  // Auto-refresh effect
  useEffect(() => {
    fetchTimeline();

    if (autoRefresh && summary?.status !== 'completed' && summary?.status !== 'failed') {
      const interval = setInterval(fetchTimeline, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [fetchTimeline, autoRefresh, refreshInterval, summary?.status]);

  // Toggle item expansion
  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Get step icon based on category and status
  const getStepIcon = (item) => {
    if (item.has_error) {
      return <XCircle className="h-4 w-4 text-red-500" />;
    }

    if (item.is_milestone) {
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    }

    switch (item.step_category) {
      case 'initialization':
        return <Settings className="h-4 w-4 text-blue-500" />;
      case 'execution':
        return <Zap className="h-4 w-4 text-yellow-500" />;
      case 'evaluation':
        return <Activity className="h-4 w-4 text-purple-500" />;
      case 'completion':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  // Get step color based on category and status
  const getStepColor = (item) => {
    if (item.has_error) return 'border-red-200 bg-red-50';
    if (item.is_milestone) return 'border-green-200 bg-green-50';

    switch (item.step_category) {
      case 'initialization':
        return 'border-blue-200 bg-blue-50';
      case 'execution':
        return 'border-yellow-200 bg-yellow-50';
      case 'evaluation':
        return 'border-purple-200 bg-purple-50';
      case 'completion':
        return 'border-green-200 bg-green-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  // Format duration
  const formatDuration = (ms) => {
    if (!ms) return null;
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  // Render timeline summary
  const renderSummary = () => {
    if (!summary) return null;

    const statusIcon = {
      'completed': <CheckCircle className="h-5 w-5 text-green-500" />,
      'failed': <XCircle className="h-5 w-5 text-red-500" />,
      'in_progress': <Activity className="h-5 w-5 text-blue-500 animate-pulse" />,
    }[summary.status] || <Clock className="h-5 w-5 text-gray-500" />;

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            {statusIcon}
            <h3 className="text-lg font-semibold capitalize">
              {summary.status.replace('_', ' ')}
            </h3>
            {isRefreshing && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            )}
          </div>
          {summary.progress_percentage > 0 && (
            <div className="text-sm text-gray-600">
              {summary.progress_percentage}% complete
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-gray-500">Duration</div>
            <div className="font-medium">
              {summary.duration_human || formatDuration(summary.duration_ms) || 'N/A'}
            </div>
          </div>
          <div>
            <div className="text-gray-500">Steps</div>
            <div className="font-medium">{summary.total_steps}</div>
          </div>
          <div>
            <div className="text-gray-500">Errors</div>
            <div className={`font-medium ${summary.error_count > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {summary.error_count}
            </div>
          </div>
          <div>
            <div className="text-gray-500">Milestones</div>
            <div className="font-medium">{summary.milestone_count}</div>
          </div>
        </div>

        {summary.progress_percentage > 0 && (
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${summary.progress_percentage}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render timeline item
  const renderTimelineItem = (item, index) => {
    const isExpanded = expandedItems.has(item.id);
    const hasContext = item.context && Object.keys(item.context).length > 0;

    return (
      <div key={item.id} className="relative">
        {/* Timeline line */}
        {index < timeline.length - 1 && (
          <div className="absolute left-6 top-12 w-0.5 h-8 bg-gray-200"></div>
        )}

        <div className={`relative flex items-start space-x-3 p-3 rounded-lg border ${getStepColor(item)}`}>
          {/* Step icon */}
          <div className="flex-shrink-0 mt-0.5">
            {getStepIcon(item)}
          </div>

          {/* Step content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <h4 className="text-sm font-medium text-gray-900">
                  {item.message}
                </h4>
                {item.is_milestone && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    Milestone
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                {item.duration && (
                  <span>{formatDuration(item.duration)}</span>
                )}
                <span>{item.relative_time}</span>
                {hasContext && showDetails && (
                  <button
                    onClick={() => toggleExpanded(item.id)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Step details */}
            {!compact && (
              <div className="mt-1 text-xs text-gray-600">
                <span className="capitalize">{item.step_category}</span>
                {item.time_since_previous && (
                  <span className="ml-2">
                    (+{formatDuration(item.time_since_previous)})
                  </span>
                )}
              </div>
            )}

            {/* Expanded context */}
            {isExpanded && hasContext && (
              <div className="mt-3 p-3 bg-white rounded border">
                <h5 className="text-xs font-medium text-gray-700 mb-2">Details</h5>
                <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                  {JSON.stringify(item.context, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-600">Loading timeline...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error Loading Timeline</h3>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={fetchTimeline}
          className="mt-3 text-sm text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (timeline.length === 0) {
    return (
      <div className="text-center p-8 text-gray-500">
        <Clock className="h-8 w-8 mx-auto mb-2" />
        <p>No timeline data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {renderSummary()}

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Execution Timeline</h3>
          <p className="text-sm text-gray-600 mt-1">
            Detailed log of evaluation execution steps
          </p>
        </div>

        <div className="p-4 space-y-3">
          {timeline.map((item, index) => renderTimelineItem(item, index))}
        </div>
      </div>
    </div>
  );
};

export default EvaluationTimeline;